import './sentry';
import './bootstrap';
import { captureNetworkError } from './utils/capture-error';
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
import registerValidateDirective from './directives/validate';
import registerTrackFormDirective from './directives/track-form';

// Import offline storage module (registers Alpine data component)
import './offline-storage';

// Import view transitions module
import { initViewTransitions } from './utils/view-transitions';

// Import globally-needed Alpine data components
import carouselLogo from './components/carousel-logo';
import carouselSelectionScoring from './components/carousel-selection-scoring';
import countdownTimer from './components/countdown-timer';
import initPwaPrompt from './components/pwa-prompt';

// Import globally-needed Alpine stores
import { initAppStore } from './stores/app-store';

// Register Alpine plugins
Alpine.plugin(collapse);
Alpine.plugin(focus);

// Register Alpine directives
registerValidateDirective(Alpine);
registerTrackFormDirective(Alpine);

// Register globally-needed Alpine data components
Alpine.data('carouselLogo', carouselLogo);
Alpine.data('carouselSelectionScoring', carouselSelectionScoring);
Alpine.data('countdownTimer', countdownTimer);

// ============================================================================
// Page-Specific Module Loading
// ============================================================================
// Detect page type + feature gates eagerly so dynamic imports start fetching
// immediately. We await the imports BEFORE calling Alpine.start() so that all
// data components and stores are registered synchronously — no race condition.

const pageType = document.querySelector('[data-page]')?.dataset.page;
const needsBanter = document.querySelector('[x-data*="banterPanel"]') !== null;
const needsShare = document.querySelector('[data-share-aware]') !== null;
const isAuthenticated = document.body.hasAttribute('data-authenticated');

/** @type {Array<Promise<void>>} */
const pageImports = [];

if (pageType === 'board') {
  pageImports.push(
    Promise.all([
      import('./components/board-wrapper'),
      import('./components/board-carousel'),
      import('./components/room-association'),
      import('./components/import-modal'),
      import('./stores/draft-board-store'),
      import('./stores/live-draft-store'),
      import('./stores/draft-results-store'),
    ]).then(
      ([
        boardWrapper,
        boardCarousel,
        roomAssociation,
        importModal,
        draftBoard,
        liveDraft,
        draftResults,
      ]) => {
        document.addEventListener('alpine:init', () => {
          Alpine.data('boardWrapper', boardWrapper.default);
          Alpine.data('boardCarousel', boardCarousel.default);
          Alpine.data('roomAssociation', roomAssociation.default);
          importModal.default(Alpine);
          draftBoard.initDraftBoardStore(Alpine);
          liveDraft.initLiveDraftStore(Alpine);
          draftResults.initDraftResultsStore(Alpine);
        });
      }
    )
  );
} else if (pageType === 'dashboard') {
  pageImports.push(
    import('./stores/dashboard-store').then(({ initDashboardStore }) => {
      document.addEventListener('alpine:init', () => {
        initDashboardStore(Alpine);
      });
    })
  );
} else if (pageType === 'entries') {
  pageImports.push(
    import('./components/entry-filters').then(({ default: entryFilters }) => {
      document.addEventListener('alpine:init', () => {
        Alpine.data('entryFilters', entryFilters);
      });
    })
  );
}

if (needsBanter) {
  pageImports.push(
    import('./components/banter-panel').then(({ default: initBanterPanel }) => {
      document.addEventListener('alpine:init', () => initBanterPanel(Alpine));
    })
  );
}

if (needsShare) {
  pageImports.push(
    import('./stores/share-store').then(({ initShareStore }) => {
      document.addEventListener('alpine:init', () => initShareStore(Alpine));
    })
  );
}

if (isAuthenticated) {
  pageImports.push(
    import('./stores/notifications-store').then(({ initNotificationsStore }) => {
      document.addEventListener('alpine:init', () => initNotificationsStore(Alpine));
    })
  );
}

// App store is globally needed and small — keep it eager
document.addEventListener('alpine:init', () => {
  initAppStore(Alpine);
});

// Await page imports, then start Alpine. For pages with no gates,
// Promise.all([]) resolves as a microtask — effectively zero delay.
window.Alpine = Alpine;
Promise.all(pageImports)
  .catch((error) => {
    console.error('[App] Failed to load page modules:', error);
  })
  .then(() => {
    Alpine.start();
  });

// ============================================================================
// PWA Install Prompt (vanilla JS, no Alpine dependency)
// ============================================================================
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPwaPrompt);
} else {
  initPwaPrompt();
}

// ============================================================================
// View Transitions
// ============================================================================
// Initialize cross-document view transitions for smooth page navigation.
// This must run early to set up event listeners before navigation occurs.
initViewTransitions();

// ============================================================================
// Service Worker Registration
// ============================================================================

/** @type {number|null} SW update check interval ID */
let swUpdateInterval = null;

/** @type {number|null} Cache prune interval ID */
let cachePruneInterval = null;

if ('serviceWorker' in navigator && !import.meta.env.DEV) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        console.log('[App] Service Worker registered:', registration.scope);

        // Check for updates periodically (every 60 seconds)
        swUpdateInterval = setInterval(() => {
          registration.update();
        }, 60000);

        // Prune cache periodically (every 30 minutes)
        cachePruneInterval = setInterval(() => {
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'PRUNE_CACHE',
            });
          }
        }, 1800000);

        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (
                newWorker.state === 'installed' &&
                navigator.serviceWorker.controller
              ) {
                // New service worker available, could show update prompt here
                console.log('[App] New service worker available');
              }
            });
          }
        });
      })
      .catch((error) => {
        console.error('[App] Service Worker registration failed:', error);
        captureNetworkError(error, { component: 'service-worker' });
      });

    // Listen for controller changes (when new SW takes over)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log(
        '[App] Service Worker controller changed — reloading for fresh content'
      );
      window.location.reload();
    });
  });
}

// ============================================================================
// Page Unload Cleanup
// ============================================================================
window.addEventListener('beforeunload', () => {
  // Clear SW intervals
  if (swUpdateInterval) {
    clearInterval(swUpdateInterval);
    swUpdateInterval = null;
  }
  if (cachePruneInterval) {
    clearInterval(cachePruneInterval);
    cachePruneInterval = null;
  }

  // Destroy notifications store (clears polling interval + visibility listener)
  window.Alpine?.store('notifications')?.destroy();

  // Disconnect WebSocket
  window.Echo?.disconnect();
});
